import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { X, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    question: "Apa dampak yang ingin bisnis Anda capai?",
    type: 'choice',
    field: 'impact_goal',
    options: [
      { value: 'brand-awareness', label: 'Meningkatkan Brand Awareness' },
      { value: 'lead-generation', label: 'Menghasilkan Leads & Prospek' },
      { value: 'revenue', label: 'Meningkatkan Revenue Langsung' },
      { value: 'trust', label: 'Membangun Kepercayaan & Kredibilitas' },
    ],
  },
  {
    question: "Siapa audiens ideal Anda?",
    type: 'select',
    field: 'audience_industry',
    options: [
      { value: 'b2b-tech', label: 'B2B — Teknologi & SaaS' },
      { value: 'b2b-services', label: 'B2B — Jasa Profesional' },
      { value: 'b2c-retail', label: 'B2C — Retail & E-commerce' },
      { value: 'b2c-fnb', label: 'B2C — F&B & Lifestyle' },
      { value: 'b2c-education', label: 'B2C — Pendidikan' },
      { value: 'mixed', label: 'Campuran B2B & B2C' },
    ],
  },
  {
    question: "Apa ketakutan terbesar dalam digitalisasi?",
    type: 'choice',
    field: 'digitalization_fear',
    options: [
      { value: 'cost', label: 'Biaya terlalu mahal' },
      { value: 'complexity', label: 'Terlalu rumit untuk dikelola' },
      { value: 'results', label: 'Tidak yakin akan hasilnya' },
      { value: 'security', label: 'Keamanan data' },
      { value: 'none', label: 'Tidak ada—saya siap!' },
    ],
  },
  {
    question: "Berapa % pelanggan Anda berasal dari online?",
    type: 'slider',
    field: 'online_customer_pct',
  },
  {
    question: "Sudahkah bisnis Anda punya narasi brand yang jelas?",
    type: 'choice',
    field: 'has_brand_narrative',
    options: [
      { value: 'ya', label: 'Ya, sudah sangat jelas' },
      { value: 'ragu', label: 'Ragu-ragu, masih mencari' },
      { value: 'belum', label: 'Belum sama sekali' },
    ],
  },
];

export default function DiscoveryTool({ onClose }) {
  const { theme } = useTheme();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ online_customer_pct: 30 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const current = STEPS[step];

  const handleAnswer = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    if (!current) return false;
    return answers[current.field] !== undefined && answers[current.field] !== '';
  };

  const handleNext = async () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/discovery`, {
          impact_goal: answers.impact_goal || '',
          audience_industry: answers.audience_industry || '',
          digitalization_fear: answers.digitalization_fear || '',
          online_customer_pct: answers.online_customer_pct || 0,
          has_brand_narrative: answers.has_brand_narrative || '',
        });
        setResult(res.data?.data || res.data);
      } catch (e) {
        console.error(e);
        setResult({
          score: 55,
          level: 'Berkembang',
          insights: 'Terjadi kesalahan saat menghubungi server. Tapi jangan khawatir—jadwalkan Sesi Discovery gratis untuk assessment mendalam.',
          recommendations: ['Jadwalkan Sesi Discovery gratis bersama Bewhy'],
        });
      }
      setLoading(false);
    }
  };

  return (
    <div className="glass-card p-6 lg:p-8 relative" data-testid="discovery-tool">
      <button
        onClick={onClose}
        data-testid="discovery-close"
        className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
        style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: 'var(--text-secondary)' }}
      >
        <X size={16} />
      </button>

      {/* Progress */}
      {!result && (
        <div className="flex gap-1.5 mb-6" data-testid="discovery-progress">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className="h-1 rounded-full flex-1 transition-all duration-500"
              style={{
                backgroundColor: i <= step ? 'var(--accent-primary)' : theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              }}
            />
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {result ? (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="discovery-result"
          >
            <div className="text-center space-y-4">
              <span className="font-mono text-xs tracking-widest uppercase" style={{ color: 'var(--accent-primary)' }}>
                Skor Kejernihan Visi Digital
              </span>
              <div className="relative w-32 h-32 mx-auto">
                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                  <circle cx="60" cy="60" r="50" fill="none" stroke={theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} strokeWidth="8" />
                  <motion.circle
                    cx="60" cy="60" r="50" fill="none"
                    stroke="var(--accent-primary)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={314}
                    initial={{ strokeDashoffset: 314 }}
                    animate={{ strokeDashoffset: 314 - (314 * result.score / 100) }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-heading text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                    {result.score}
                  </span>
                  <span className="font-mono text-[10px] tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                    / 100
                  </span>
                </div>
              </div>
              <div
                className="inline-block px-4 py-1.5 rounded-full font-mono text-xs tracking-wider"
                style={{
                  background: theme === 'dark' ? 'rgba(0,255,133,0.1)' : 'rgba(13,148,136,0.1)',
                  color: 'var(--accent-primary)',
                }}
              >
                {result.level}
              </div>
            </div>

            <div className="text-left py-2">
              <MarkdownRenderer content={result.insights} />
            </div>

            <div className="space-y-2">
              <span className="font-mono text-xs tracking-wider uppercase" style={{ color: 'var(--text-secondary)' }}>Rekomendasi:</span>
              {result.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                  <ChevronRight size={14} style={{ color: 'var(--accent-primary)', marginTop: 3, flexShrink: 0 }} />
                  {rec}
                </div>
              ))}
            </div>

            <a
              href="https://wa.me/6281999934451?text=Halo%20Bewhy,%20saya%20baru%20saja%20mengisi%20Digital%20Vision%20Check%20dan%20ingin%20konsultasi%20lebih%20lanjut"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="discovery-cta-consult"
              className="block w-full py-3 rounded-xl text-center font-heading font-semibold text-sm tracking-wide uppercase transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: 'var(--accent-primary)',
                color: theme === 'dark' ? '#0D0D0D' : '#FFFFFF',
              }}
            >
              Konsultasi Gratis
            </a>
          </motion.div>
        ) : (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div>
              <span className="font-mono text-xs tracking-widest" style={{ color: 'var(--accent-primary)' }}>
                {step + 1} / {STEPS.length}
              </span>
              <h3 className="font-heading text-lg lg:text-xl font-semibold mt-2" style={{ color: 'var(--text-primary)' }}>
                {current.question}
              </h3>
            </div>

            {current.type === 'choice' && (
              <div className="space-y-2">
                {current.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleAnswer(current.field, opt.value)}
                    data-testid={`discovery-option-${opt.value}`}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm transition-all duration-200"
                    style={{
                      backgroundColor: answers[current.field] === opt.value
                        ? (theme === 'dark' ? 'rgba(0,255,133,0.1)' : 'rgba(13,148,136,0.1)')
                        : (theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'),
                      border: `1px solid ${answers[current.field] === opt.value ? 'var(--accent-primary)' : 'var(--border-card)'}`,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {current.type === 'select' && (
              <Select
                value={answers[current.field] || ''}
                onValueChange={(val) => handleAnswer(current.field, val)}
              >
                <SelectTrigger data-testid="discovery-select">
                  <SelectValue placeholder="Pilih industri..." />
                </SelectTrigger>
                <SelectContent>
                  {current.options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {current.type === 'slider' && (
              <div className="space-y-4">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={answers[current.field] || 30}
                  onChange={(e) => handleAnswer(current.field, parseInt(e.target.value))}
                  data-testid="discovery-slider"
                  className="w-full accent-current"
                  style={{ accentColor: 'var(--accent-primary)' }}
                />
                <div className="flex justify-between text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                  <span>0%</span>
                  <span className="font-semibold text-base" style={{ color: 'var(--accent-primary)' }}>
                    {answers[current.field] || 30}%
                  </span>
                  <span>100%</span>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  data-testid="discovery-prev"
                  className="px-4 py-3 rounded-xl text-sm font-medium transition-all"
                  style={{
                    border: '1px solid var(--border-card)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!canProceed() || loading}
                data-testid="discovery-next"
                className="flex-1 py-3 rounded-xl font-heading font-semibold text-sm tracking-wide uppercase transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  backgroundColor: canProceed() ? 'var(--accent-primary)' : (theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                  color: canProceed() ? (theme === 'dark' ? '#0D0D0D' : '#FFFFFF') : 'var(--text-secondary)',
                }}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : (step === STEPS.length - 1 ? 'Lihat Hasil' : 'Selanjutnya')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
